import React from "react";
import styled from "styled-components";
import { TableProps } from "../types/Table.ts";

const TableWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 31, 82, 0.1);
  overflow-x: auto;
  overflow-y: visible;
  margin-top: 1rem;
  transition: background 0.3s ease, box-shadow 0.3s ease;
  width: 100%;
  box-sizing: border-box;
  
  /* Hide scrollbar but keep scroll functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  
  .dark-mode & {
    background: #2a2a3e;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  table-layout: fixed; /* Fixed layout ensures consistent column widths */

  tbody tr {
    transition: all 0.2s ease;
    background-color: white;
  }

  .dark-mode & tbody tr {
    background-color: #2a2a3e;
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  .dark-mode & tbody tr:nth-child(even) {
    background-color: #1f1f2e;
  }

  tbody tr:hover {
    background-color: #e8f5e9;
    transform: scale(1.01);
    box-shadow: 0 2px 4px rgba(100, 218, 76, 0.2);
    z-index: 1;
    position: relative;
  }

  .dark-mode & tbody tr:hover {
    background-color: #3a3a4e;
    box-shadow: 0 2px 4px rgba(100, 218, 76, 0.3);
  }
`;

const Th = styled.th<{ $width?: string; $minWidth?: string; $maxWidth?: string }>`
  border-bottom: 2px solid #001f52;
  padding: 0.5rem 0.5rem;
  text-align: left;
  color: #001f52;
  background-color: #f5f7fa;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  ${({ $width }) => $width ? `width: ${$width};` : ''}
  ${({ $minWidth }) => $minWidth ? `min-width: ${$minWidth};` : ''}
  ${({ $maxWidth }) => $maxWidth ? `max-width: ${$maxWidth};` : ''}
  white-space: normal;
  word-break: keep-all;
  line-height: 1.3;
  vertical-align: bottom;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;

  .dark-mode & {
    border-bottom-color: #64da4c;
    color: #e0e0e0;
    background-color: #1f1f2e;
  }
`;

const Td = styled.td<{ $noTruncate?: boolean; $allowWrap?: boolean }>`
  border-bottom: 1px solid #e0e0e0;
  padding: 0.5rem 0.5rem;
  color: #333;
  font-size: 0.9rem;
  transition: color 0.3s ease, border-color 0.3s ease;
  ${({ $noTruncate, $allowWrap }) => {
    if ($noTruncate) return '';
    if ($allowWrap) return 'word-wrap: break-word; white-space: normal;';
    return 'overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
  }}

  .dark-mode & {
    border-bottom-color: #3a3a4e;
    color: #d0d0d0;
  }
`;

export default function Table<T extends { id: string | number }>({
  data,
  columns,
  onRowClick,
}: TableProps<T>) {
  return (
    <TableWrapper>
      <StyledTable>
        <thead>
          <tr>
            {columns.map((col) => (
              <Th 
                key={String(col.key)} 
                $width={col.width}
                $minWidth={col.minWidth}
                $maxWidth={col.maxWidth}
              >
                {col.label}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map(row => (
            <tr 
              key={row.id}
              onClick={() => onRowClick?.(row)}
              style={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              {columns.map(col => (
                <Td 
                  key={String(col.key)}
                  $noTruncate={col.key === 'userRating' || col.key === 'evaluated'}
                  $allowWrap={col.key === 'userComment' || col.key === 'latestAdminComment'}
                >
                  {col.render ? col.render(row[col.key], row) : String(row[col.key])}
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
}
